import React, { useEffect, useRef, useState } from 'react';

type SceneId = 'marca' | 'mapa' | 'cobertura' | 'registro' | 'webmovil' | 'cierre';

interface Scene {
  id: SceneId;
  duration: number;
  label: string;
}

const SCENES: Scene[] = [
  { id: 'marca', duration: 4000, label: 'Marca' },
  { id: 'mapa', duration: 6000, label: 'Mapa Nacional' },
  { id: 'cobertura', duration: 5000, label: 'Cobertura' },
  { id: 'registro', duration: 7500, label: 'Registro en Campo' },
  { id: 'webmovil', duration: 5000, label: 'Web y Móvil' },
  { id: 'cierre', duration: 3500, label: 'Cierre' },
];

function SceneMarca({ t }: { t: number }) {
  const opacity = Math.min(1, t / 0.3);
  const scale = 0.8 + 0.2 * Math.min(1, t / 0.5);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity, transform: `scale(${scale})` }}>
      <div style={{ position: 'relative', marginBottom: 32 }}>
        {[120, 180, 240].map((s, i) => (
          <div key={s} style={{ position: 'absolute', width: s, height: s, borderRadius: '50%', border: '1px solid rgba(96,165,250,0.2)', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', animation: `ringSpin ${10 + i * 5}s linear infinite` }} />
        ))}
        <div style={{ position: 'relative', zIndex: 1, width: 80, height: 80, borderRadius: 24, background: 'rgba(96,165,250,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="40" height="40" viewBox="0 0 64 64" fill="none">
            <circle cx="32" cy="32" r="27" stroke="#60A5FA" strokeWidth="3"/>
            <circle cx="24" cy="24" r="4" fill="#60A5FA"/>
          </svg>
        </div>
      </div>
      <h1 style={{ fontSize: 56, fontWeight: 800, color: '#fff', margin: '0 0 12px', letterSpacing: '-0.04em' }}>ComuniData</h1>
      <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', margin: 0 }}>Plataforma de gestión territorial</p>
    </div>
  );
}

function SceneMapa({ t }: { t: number }) {
  const pins = [
    { x: 35, y: 45, cat: '#dc2626', delay: 0.1 },
    { x: 60, y: 30, cat: '#ca8a04', delay: 0.25 },
    { x: 25, y: 65, cat: '#22c55e', delay: 0.4 },
    { x: 70, y: 60, cat: '#dc2626', delay: 0.55 },
    { x: 50, y: 50, cat: '#0ea5e9', delay: 0.7 },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 40 }}>
      <h2 style={{ fontSize: 36, fontWeight: 800, color: '#fff', margin: '0 0 24px', opacity: Math.min(1, t / 0.2) }}>Georeferenciación en tiempo real</h2>
      <div style={{ width: '100%', maxWidth: 500, aspectRatio: '16/9', background: 'rgba(255,255,255,0.05)', borderRadius: 20, position: 'relative', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
        {/* Fake map grid */}
        <svg width="100%" height="100%" style={{ position: 'absolute', opacity: 0.15 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <React.Fragment key={i}>
              <line x1={`${i * 14}%`} y1="0" x2={`${i * 14}%`} y2="100%" stroke="#60A5FA" strokeWidth="0.5" />
              <line x1="0" y1={`${i * 14}%`} x2="100%" y2={`${i * 14}%`} stroke="#60A5FA" strokeWidth="0.5" />
            </React.Fragment>
          ))}
        </svg>
        {pins.map((p, i) => {
          const show = t > p.delay;
          return (
            <div key={i} style={{ position: 'absolute', left: `${p.x}%`, top: `${p.y}%`, transform: 'translate(-50%,-50%)', opacity: show ? 1 : 0, transition: 'all 400ms', scale: show ? '1' : '0' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: p.cat, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 12px ${p.cat}88` }}>
                <i className="ti ti-map-pin" style={{ color: '#fff', fontSize: 13 }} />
              </div>
              {show && (
                <div style={{ position: 'absolute', bottom: '120%', left: '50%', transform: 'translateX(-50%)', background: 'rgba(10,22,40,0.9)', borderRadius: 8, padding: '4px 8px', fontSize: 10, color: '#fff', whiteSpace: 'nowrap', animation: 'popIn 200ms ease-out' }}>
                  Incidencia #{i + 1}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SceneCobertura({ t }: { t: number }) {
  const cards = [
    { label: 'Vialidad', value: '47', unit: 'reportes', color: '#dc2626', icon: 'ti-road', pct: 78 },
    { label: 'Plazas y Parques', value: '23', unit: 'reportes', color: '#22c55e', icon: 'ti-trees', pct: 55 },
    { label: 'Alumbrado', value: '31', unit: 'reportes', color: '#ca8a04', icon: 'ti-bulb', pct: 66 },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 40 }}>
      <h2 style={{ fontSize: 36, fontWeight: 800, color: '#fff', margin: '0 0 32px', opacity: Math.min(1, t / 0.2) }}>Cobertura institucional</h2>
      <div style={{ display: 'flex', gap: 16, width: '100%', maxWidth: 640 }}>
        {cards.map((c, i) => {
          const appear = t > i * 0.2;
          return (
            <div key={c.label} style={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 20, padding: 24, border: `1px solid ${c.color}33`, opacity: appear ? 1 : 0, transform: appear ? 'translateY(0)' : 'translateY(20px)', transition: 'all 500ms' }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: `${c.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                <i className={`ti ${c.icon}`} style={{ color: c.color, fontSize: 18 }} />
              </div>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{c.value}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 14 }}>{c.unit}</div>
              <div style={{ height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                <div style={{ height: '100%', width: appear ? `${c.pct}%` : '0%', background: c.color, borderRadius: 2, transition: 'width 800ms ease' }} />
              </div>
              <div style={{ fontSize: 10, color: c.color, fontWeight: 700, marginTop: 5 }}>{c.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SceneRegistro({ t }: { t: number }) {
  const step = t < 0.25 ? 0 : t < 0.55 ? 1 : t < 0.8 ? 2 : 3;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 60, padding: 40 }}>
      <div style={{ opacity: Math.min(1, t / 0.15) }}>
        <h2 style={{ fontSize: 36, fontWeight: 800, color: '#fff', margin: '0 0 12px' }}>Registro en campo</h2>
        <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)', margin: '0 0 28px', maxWidth: 260 }}>Brigadistas reportan hallazgos desde el terreno</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {['Categorizar hallazgo', 'Describir el problema', 'Enviar reporte'].map((s, i) => (
            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: i < step ? '#22c55e' : i === step ? 'rgba(96,165,250,0.3)' : 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: i < step ? '#fff' : i === step ? '#60A5FA' : 'rgba(255,255,255,0.3)', transition: 'all 400ms', flexShrink: 0 }}>
                {i < step ? <i className="ti ti-check" style={{ fontSize: 11 }} /> : i + 1}
              </div>
              <span style={{ fontSize: 13, color: i <= step ? '#fff' : 'rgba(255,255,255,0.35)', fontWeight: i === step ? 700 : 400 }}>{s}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Phone mockup */}
      <div style={{ width: 180, height: 320, borderRadius: 36, background: '#1a1a2e', border: '8px solid rgba(255,255,255,0.1)', position: 'relative', overflow: 'hidden', flexShrink: 0, boxShadow: '0 24px 60px rgba(0,0,0,0.6)' }}>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: '#0d9488', marginBottom: 12, textAlign: 'center', letterSpacing: '0.1em' }}>REGISTRAR HALLAZGO</div>
          <div style={{ height: 28, background: step >= 1 ? 'rgba(220,38,38,0.2)' : 'rgba(255,255,255,0.05)', borderRadius: 8, marginBottom: 8, border: step >= 1 ? '1px solid rgba(220,38,38,0.4)' : 'none', display: 'flex', alignItems: 'center', paddingLeft: 8, gap: 6, transition: 'all 400ms' }}>
            {step >= 1 && <i className="ti ti-road" style={{ color: '#dc2626', fontSize: 12 }} />}
            <span style={{ fontSize: 10, color: step >= 1 ? '#dc2626' : 'rgba(255,255,255,0.2)', fontWeight: 700 }}>{step >= 1 ? 'Vialidad' : 'Categoría'}</span>
          </div>
          <div style={{ height: 52, background: 'rgba(255,255,255,0.05)', borderRadius: 8, marginBottom: 8, padding: 8, transition: 'all 400ms' }}>
            {step >= 2 && <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', lineHeight: 1.4, display: 'block' }}>Bache profundo en Av. Principal, frente a la plaza...</span>}
          </div>
          <div style={{ height: 28, background: step >= 3 ? '#22c55e' : 'rgba(29,78,216,0.3)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 400ms', boxShadow: step >= 3 ? '0 4px 12px rgba(34,197,94,0.4)' : 'none' }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: '#fff' }}>{step >= 3 ? '✓ Enviado' : 'Enviar'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SceneWebMovil({ t }: { t: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 40 }}>
      <h2 style={{ fontSize: 36, fontWeight: 800, color: '#fff', margin: '0 0 8px', opacity: Math.min(1, t / 0.2) }}>Disponible en todas las plataformas</h2>
      <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.45)', margin: '0 0 32px' }}>Misma experiencia, cualquier dispositivo</p>
      <div style={{ display: 'flex', gap: 20 }}>
        {[
          { icon: 'ti-device-desktop', label: 'Web App', color: '#60A5FA', delay: 0 },
          { icon: 'ti-device-mobile', label: 'iOS & Android', color: '#a78bfa', delay: 0.2 },
          { icon: 'ti-map', label: 'Leaflet Maps', color: '#34d399', delay: 0.4 },
        ].map(item => {
          const show = t > item.delay;
          return (
            <div key={item.label} style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 20, padding: 28, textAlign: 'center', flex: 1, opacity: show ? 1 : 0, transform: show ? 'translateY(0)' : 'translateY(24px)', transition: 'all 500ms', border: `1px solid ${item.color}33` }}>
              <i className={`ti ${item.icon}`} style={{ fontSize: 32, color: item.color, display: 'block', marginBottom: 14 }} />
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{item.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SceneCierre({ t }: { t: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center' }}>
      <div style={{ opacity: Math.min(1, t / 0.4), transform: `scale(${0.8 + 0.2 * Math.min(1, t / 0.4)})` }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🌐</div>
        <h1 style={{ fontSize: 48, fontWeight: 800, color: '#fff', margin: '0 0 14px', letterSpacing: '-0.04em' }}>ComuniData</h1>
        <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.55)', margin: '0 0 28px' }}>Gestión territorial inteligente para tu ciudad</p>
        <div style={{ padding: '13px 28px', borderRadius: 16, background: 'linear-gradient(135deg,#1D4ED8,#60A5FA)', display: 'inline-block', fontSize: 14, fontWeight: 700, color: '#fff', boxShadow: '0 8px 24px rgba(29,78,216,0.5)' }}>
          comunidata.gob.ve
        </div>
      </div>
    </div>
  );
}

export default function VideoPage() {
  const [playing, setPlaying] = useState(false);
  const [sceneIdx, setSceneIdx] = useState(0);
  const [sceneT, setSceneT] = useState(0);
  const [totalT, setTotalT] = useState(0);
  const rafRef = useRef<number>(0);
  const startRef = useRef<number>(0);
  const sceneStartRef = useRef<number>(0);

  const totalDuration = SCENES.reduce((a, s) => a + s.duration, 0);

  useEffect(() => {
    if (!playing) return;
    let lastTime = performance.now();

    function tick(now: number) {
      const elapsed = now - startRef.current;
      setTotalT(Math.min(elapsed / totalDuration, 1));

      let acc = 0;
      for (let i = 0; i < SCENES.length; i++) {
        if (elapsed < acc + SCENES[i].duration) {
          setSceneIdx(i);
          setSceneT(Math.min(1, (elapsed - acc) / SCENES[i].duration));
          break;
        }
        acc += SCENES[i].duration;
      }

      if (elapsed < totalDuration) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setPlaying(false);
        setSceneIdx(SCENES.length - 1);
        setSceneT(1);
      }
    }

    startRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing]);

  function restart() {
    setSceneIdx(0);
    setSceneT(0);
    setTotalT(0);
    setPlaying(true);
  }

  const scene = SCENES[sceneIdx];

  return (
    <div style={{ width: '100vw', height: '100vh', background: '#050d1a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'Manrope, sans-serif' }}>
      {/* Video container */}
      <div style={{ width: '100%', maxWidth: 800, aspectRatio: '16/9', background: 'linear-gradient(135deg,#0a1628,#0d2044)', borderRadius: 20, overflow: 'hidden', position: 'relative', boxShadow: '0 32px 80px rgba(0,0,0,0.8)', maxHeight: '70vh' }}>
        {/* Background rings */}
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          {[300, 480, 640].map((s, i) => (
            <div key={s} style={{ position: 'absolute', width: s, height: s, borderRadius: '50%', border: `1px solid rgba(96,165,250,${0.06 - i * 0.015})`, top: '50%', left: '50%', transform: 'translate(-50%,-50%)', animation: `ringSpin ${20 + i * 8}s linear infinite` }} />
          ))}
        </div>

        {/* Scene content */}
        <div style={{ position: 'relative', zIndex: 2, height: '100%' }}>
          {sceneIdx === 0 && <SceneMarca t={sceneT} />}
          {sceneIdx === 1 && <SceneMapa t={sceneT} />}
          {sceneIdx === 2 && <SceneCobertura t={sceneT} />}
          {sceneIdx === 3 && <SceneRegistro t={sceneT} />}
          {sceneIdx === 4 && <SceneWebMovil t={sceneT} />}
          {sceneIdx === 5 && <SceneCierre t={sceneT} />}
        </div>

        {/* Play overlay */}
        {!playing && totalT === 0 && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', zIndex: 10 }}>
            <button onClick={restart} style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '2px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, color: '#fff', cursor: 'pointer' }}>
              <i className="ti ti-player-play" />
            </button>
          </div>
        )}

        {/* Scene label */}
        <div style={{ position: 'absolute', top: 16, left: 16, zIndex: 10, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', borderRadius: 8, padding: '4px 10px', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.06em' }}>
          {scene.label.toUpperCase()}
        </div>
      </div>

      {/* Controls */}
      <div style={{ marginTop: 20, width: '100%', maxWidth: 800, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Progress bar */}
        <div style={{ height: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden', cursor: 'pointer' }}>
          <div style={{ height: '100%', width: `${totalT * 100}%`, background: 'linear-gradient(90deg,#1D4ED8,#60A5FA)', borderRadius: 2, transition: 'width 100ms linear' }} />
        </div>

        {/* Buttons + scene dots */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={playing ? () => setPlaying(false) : restart}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', borderRadius: 12, background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 12, fontWeight: 700 }}>
            <i className={`ti ${playing ? 'ti-player-pause' : 'ti-player-play'}`} style={{ fontSize: 14 }} />
            {playing ? 'Pausar' : totalT > 0 ? 'Reanudar' : 'Reproducir'}
          </button>

          <div style={{ display: 'flex', gap: 6 }}>
            {SCENES.map((s, i) => (
              <button key={s.id} onClick={() => { /* jump */ }}
                style={{ width: 6, height: 6, borderRadius: 3, background: i === sceneIdx ? '#60A5FA' : 'rgba(255,255,255,0.2)' }} />
            ))}
          </div>

          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontWeight: 600 }}>31s</span>
        </div>
      </div>
    </div>
  );
}
